/*
 * Copyright (c) 2025. Orion Analytics. All Rights Reserved
 */

import React, { useEffect, useState } from "react";
import Plot from "react-plotly.js";

import Stockchart from "../stockchart_plotly";
import { fetchMarketSentiment } from "../../api";
import MetricAssistant from "../common/MetricAssistant";

export default function MarketSentiment({
activeWidget
}) {


const [sentimentData, setSentimentData] =
    useState(null);

const [vixData, setVixData] =
    useState([]);

const [pcrData, setPcrData] =
    useState([]);

const [optionFlowData, setOptionFlowData] =
    useState([]);

useEffect(() => {

    fetchMarketSentiment()
        .then(data => {

            setSentimentData(
                data.fear_greed_index?.[0]
            );

            setVixData(
                data.vix_data || []
            );

            setPcrData(
                data.put_call_ratio || []
            );

            setOptionFlowData(
                data.option_flow || []
            );

        });

}, []);

if (!sentimentData) {

    return (
        <div className="p-8">
            Loading Market Sentiment...
        </div>
    );

}

let content;

switch (activeWidget) {

    case "Market Sentiment":

        content = (
            <SentimentView
                sentimentData={sentimentData}
                pcrData={pcrData}
            />
        );
        break;

    case "Market Volatility":

        content = (
            <VolatilityView />
        );
        break;

    case "OI Indicators":

        content = (
            <OIIndicatorsView
                vixData={vixData}
                optionFlowData={optionFlowData}
            />
        );
        break;

    default:

        content = (
            <div
                className="
                    bg-white
                    rounded-3xl
                    border
                    border-slate-200
                    p-10
                "
            >
                Select a section
            </div>
        );
}

return (

    <>
        {content}

        <MetricAssistant
            title="Market Guide"
            context="market"
        />
    </>

);
}

function SentimentView({
sentimentData,
pcrData
}) {


return (

    <div className="space-y-8">

        <div
            className="
                grid
                grid-cols-1
                xl:grid-cols-2
                gap-8
            "
        >

            {/* FEAR GREED */}

            <div
                className="
                    bg-white
                    rounded-3xl
                    border
                    border-slate-200
                    shadow-sm
                    p-6
                "
            >

                <h2
                    className="
                        text-xl
                        font-bold
                        mb-6
                    "
                >
                    Fear & Greed Index
                </h2>

                <Plot
                    data={[
                        {
                            type: "indicator",
                            mode: "gauge+number",
                            value:
                                sentimentData.fear_greed_score,
                            title: {
                                text:
                                    sentimentData.sentiment
                            },
                            gauge: {
                                axis: {
                                    range: [0, 100]
                                },
                                steps: [
                                    {
                                        range: [0, 30],
                                        color: "#dc2626"
                                    },
                                    {
                                        range: [30, 70],
                                        color: "#f59e0b"
                                    },
                                    {
                                        range: [70, 100],
                                        color: "#16a34a"
                                    }
                                ]
                            }
                        }
                    ]}
                    layout={{
                        height: 450,
                        margin: {
                            l: 20,
                            r: 20,
                            t: 40,
                            b: 20
                        }
                    }}
                    config={{
                        displayModeBar: false,
                        responsive: true
                    }}
                    style={{
                        width: "100%"
                    }}
                />

            </div>

            {/* INSIGHTS */}

            <div
                className="
                    bg-white
                    rounded-3xl
                    border
                    border-slate-200
                    shadow-sm
                    p-8
                "
            >

                <h2
                    className="
                        text-xl
                        font-bold
                        mb-8
                    "
                >
                    Sentiment Interpretation
                </h2>

                <div className="space-y-5">

                    <div>
                        <strong>
                            0 - 20
                        </strong>
                        <p>
                            Extreme Fear
                        </p>
                    </div>

                    <div>
                        <strong>
                            20 - 40
                        </strong>
                        <p>
                            Fear
                        </p>
                    </div>

                    <div>
                        <strong>
                            40 - 60
                        </strong>
                        <p>
                            Neutral
                        </p>
                    </div>

                    <div>
                        <strong>
                            60 - 80
                        </strong>
                        <p>
                            Greed
                        </p>
                    </div>

                    <div>
                        <strong>
                            80 - 100
                        </strong>
                        <p>
                            Extreme Greed
                        </p>
                    </div>

                </div>

            </div>

        </div>

        {/* PCR */}

        <div
            className="
                bg-slate-950
                rounded-3xl
                border
                border-slate-800
                p-6
            "
        >

            <h2
                className="
                    text-xl
                    text-white
                    font-bold
                    mb-6
                "
            >
                Put Call Ratio Trend
            </h2>

            <Plot
                data={[
                    {
                        x:
                            pcrData.map(
                                d => d.date
                            ),

                        y:
                            pcrData.map(
                                d => d.pcr
                            ),

                        type:
                            "scatter",

                        mode:
                            "lines",

                        line: {
                            width: 3
                        },

                        name: "PCR"
                    }
                ]}
                layout={{
                    height: 500,

                    paper_bgcolor:
                        "#020617",

                    plot_bgcolor:
                        "#020617",

                    font: {
                        color:
                            "#cbd5e1"
                    },

                    margin: {
                        l: 60,
                        r: 20,
                        t: 20,
                        b: 50
                    },

                    yaxis: {
                        title: "PCR",
                        gridcolor:
                            "#1e293b"
                    },

                    xaxis: {
                        type: "date"
                    }
                }}
                config={{
                    displayModeBar:
                        false,
                    responsive:
                        true
                }}
                style={{
                    width: "100%"
                }}
            />

        </div>

    </div>

);


}

function VolatilityView() {


return (

    <div
        className="
            bg-white
            rounded-3xl
            border
            border-slate-200
            shadow-sm
            p-6
        "
    >

        <h2
            className="
                text-xl
                font-bold
                mb-6
            "
        >
            INDIA VIX Analysis
        </h2>

        <Stockchart
            symbol="INDIA VIX"
        />

    </div>

);

}

function OIIndicatorsView({
vixData,
optionFlowData
}) {


const labels =
    optionFlowData.map(
        d =>
            Math.abs(d.oi_change)
            > 200000
                ? `${d.strike}`
                : ""
    );

const parents =
    optionFlowData.map(
        () => ""
    );

const values =
    optionFlowData.map(
        d =>
            Math.abs(d.oi_change)
            > 200000
                ? Math.abs(
                      d.oi_change
                  )
                : ""
    );

const oiChanges =
    optionFlowData.map(
        d => d.oi_change
    );

const maxOI =
    Math.max(...oiChanges);

const minOI =
    Math.min(...oiChanges);

const colors =
    oiChanges.map(oi => {

        const n =
            (oi - minOI) /
            (maxOI - minOI);

        const red =
            Math.round(
                255 * (1 - n)
            );

        const green =
            Math.round(
                255 * n
            );

        return `rgb(${red},${green},0)`;

    });

return (

    <div className="space-y-8">

        <div
            className="
                bg-slate-950
                rounded-3xl
                border
                border-slate-800
                p-6
            "
        >

            <h2
                className="
                    text-xl
                    text-white
                    font-bold
                    mb-6
                "
            >
                VIX vs Market Correlation
            </h2>

            <Plot
                data={[
                    {
                        x:
                            vixData.map(
                                d => d.date
                            ),

                        y:
                            vixData.map(
                                d => d.vix
                            ),

                        type:
                            "scatter",

                        mode:
                            "lines",

                        name:
                            "VIX",

                        yaxis:
                            "y1"
                    },

                    {
                        x:
                            vixData.map(
                                d => d.date
                            ),

                        y:
                            vixData.map(
                                d =>
                                    d.index_change
                            ),

                        type:
                            "scatter",

                        mode:
                            "lines",

                        name:
                            "Nifty",

                        yaxis:
                            "y2"
                    }
                ]}
                layout={{
                    height: 650,

                    paper_bgcolor:
                        "#020617",

                    plot_bgcolor:
                        "#020617",

                    font: {
                        color:
                            "#cbd5e1"
                    },

                    yaxis: {
                        title:
                            "VIX"
                    },

                    yaxis2: {
                        title:
                            "Nifty Change",
                        overlaying:
                            "y",
                        side:
                            "right"
                    }
                }}
                config={{
                    responsive:
                        true,
                    displayModeBar:
                        false
                }}
                style={{
                    width: "100%"
                }}
            />

        </div>

        <div
            className="
                bg-white
                rounded-3xl
                border
                border-slate-200
                shadow-sm
                p-6
            "
        >

            <h2
                className="
                    text-xl
                    font-bold
                    mb-6
                "
            >
                Open Interest Heatmap
            </h2>

            <Plot
                data={[
                    {
                        type:
                            "treemap",

                        labels,

                        parents,

                        values,

                        marker: {
                            colors
                        },

                        textinfo:
                            "label+value"
                    }
                ]}
                layout={{
                    height: 700
                }}
                config={{
                    displayModeBar:
                        false,
                    responsive:
                        true
                }}
                style={{
                    width: "100%"
                }}
            />

        </div>

    </div>

);

}
